import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'sonner';

// Shadcn UI Components
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './components/ui/dialog';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Loader2, AlertCircle } from 'lucide-react';

// Icons
import { 
  Users, 
  CreditCard, 
  Crown, 
  Heart, 
  Star, 
  CheckCircle, 
  XCircle, 
  Plus,
  Trash2,
  Edit,
  DollarSign,
  UserPlus,
  Settings
} from 'lucide-react';

import { BACKEND_URL, API } from './config';

// =============================================================================
// PAYMENT SYSTEM STATUS CHECK
// =============================================================================
// This ensures payment UI only renders when backend confirms payments are ready.
// Prevents random failures from misconfigured payment systems.
// =============================================================================

let _paymentSystemReady = null; // Cache the result

export const checkPaymentSystemReady = async () => {
  if (_paymentSystemReady !== null) {
    return _paymentSystemReady;
  }
  
  try {
    const response = await axios.get(`${API}/payments/status`);
    _paymentSystemReady = response.data.ready === true;
    
    if (!_paymentSystemReady) {
      console.error('Payment system not ready:', response.data.error);
    }
    
    return _paymentSystemReady;
  } catch (error) {
    console.error('Failed to check payment system status:', error);
    _paymentSystemReady = false;
    return false;
  }
};

// Reset cache (useful for testing)
export const resetPaymentSystemCache = () => {
  _paymentSystemReady = null;
};

// =============================================================================
// SUBSCRIPTION BUTTON COMPONENT - FROZEN (Last verified: Feb 4, 2026)
// =============================================================================

// Subscription Button Component
export const SubscriptionButton = ({ 
  creatorId, 
  creatorName,
  className = "",
  variant = "default"
}) => {
  const [loading, setLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [paymentSystemReady, setPaymentSystemReady] = useState(true);

  useEffect(() => {
    // Check payment system status first
    checkPaymentSystemReady().then(ready => {
      setPaymentSystemReady(ready);
    });
    
    checkSubscriptionStatus();
    fetchSubscriptionInfo();
  }, [creatorId]);

  const checkSubscriptionStatus = async () => {
    try {
      const sessionToken = Cookies.get('session_token');
      if (!sessionToken) {
        setCheckingSubscription(false);
        return;
      }

      const response = await axios.get(`${API}/user/is-subscribed/${creatorId}`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      setIsSubscribed(response.data.is_subscribed);
    } catch (error) {
      console.error('Error checking subscription:', error);
    } finally {
      setCheckingSubscription(false);
    }
  };

  const fetchSubscriptionInfo = async () => {
    try {
      const response = await axios.get(`${API}/creator/${creatorId}/subscription-info`);
      setSubscriptionInfo(response.data);
    } catch (error) {
      console.error('Error fetching subscription info:', error);
    }
  };

  const handleSubscribe = async () => {
    if (isSubscribed) {
      toast.info('You are already subscribed to this creator!');
      return;
    }

    setLoading(true);
    try {
      const sessionToken = Cookies.get('session_token');
      if (!sessionToken) {
        toast.error('Please log in to subscribe');
        setLoading(false);
        return;
      }

      // 0 is a real price: free. `?? 0` keeps 0 intact instead of coercing it.
      const price = Number(subscriptionInfo?.subscription_price ?? 0);
      const isFreeSubscription = !(price > 0);

      if (isFreeSubscription) {
        // Free subscription — direct API call, no Stripe
        await axios.post(`${API}/subscribe/free`, {
          creator_id: creatorId
        }, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
        setIsSubscribed(true);
        toast.success(`Subscribed to ${creatorName || 'creator'}!`);
        setLoading(false);
        return;
      }

      // Paid subscription — Stripe checkout
      const response = await axios.post(`${API}/payments/checkout/subscription`, {
        creator_id: creatorId,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });

      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
      
    } catch (error) {
      console.error('Subscription error:', error);
      console.error('Error response:', error.response?.data);
      
      // Handle specific error cases
      if (error.response?.status === 401) {
        toast.error('Your session has expired. Please log in again.');
        const cookieOptions = { path: '/' };
        Cookies.remove('session_token', cookieOptions);
        Cookies.remove('user_data', cookieOptions);
      } else if (error.response?.status === 500) {
        const detail = error.response?.data?.detail || 'Server error';
        if (detail.includes('API key')) {
          toast.error('Payment system configuration error. Please try again later.');
        } else {
          toast.error(`Subscription failed: ${detail}`);
        }
      } else {
        toast.error(error.response?.data?.detail || 'Subscription failed. Please try again.');
      }
      setLoading(false);
    }
  };

  const price = Number(subscriptionInfo?.subscription_price ?? 0);
  const isFree = !(price > 0);

  if (checkingSubscription) {
    return (
      <Button disabled className={className}>
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        Checking...
      </Button>
    );
  }

  if (isSubscribed) {
    return (
      <Button disabled className={`${className} bg-green-500 hover:bg-green-600`} variant={variant}>
        <CheckCircle className="h-4 w-4 mr-2" />
        Subscribed
      </Button>
    );
  }

  // SAFEGUARD: block the UI only for PAID subscriptions when the payment system
  // is not ready. Free subscriptions use the internal no-Stripe flow, so they
  // must stay available.
  if (!paymentSystemReady && !isFree) {
    return (
      <Button disabled className={`${className} bg-gray-400`} variant={variant}>
        <AlertCircle className="h-4 w-4 mr-2" />
        Payments Unavailable
      </Button>
    );
  }

  return (
    <Button 
      onClick={handleSubscribe}
      disabled={loading}
      data-testid={isFree ? 'subscribe-free-btn' : 'subscribe-paid-btn'}
      className={`${className} ${isFree ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600'}`}
      variant={variant}
    >
      {loading ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          Processing...
        </>
      ) : (
        <>
          {isFree ? (
            <>
              <UserPlus className="h-4 w-4 mr-2" />
              Subscribe for free
            </>
          ) : (
            <>
              <Crown className="h-4 w-4 mr-2" />
              Subscribe ${price}/month
            </>
          )}
        </>
      )}
    </Button>
  );
};

// Creator Subscription Settings Modal
export const CreatorSubscriptionSettings = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    subscription_price: 0.0,
    description: 'Subscribe for exclusive content and updates',
    benefits: []
  });
  const [newBenefit, setNewBenefit] = useState('');

  useEffect(() => {
    if (open) {
      fetchSettings();
    }
  }, [open]);

  const fetchSettings = async () => {
    try {
      const sessionToken = Cookies.get('session_token');
      const response = await axios.get(`${API}/creator/subscription-settings`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const sessionToken = Cookies.get('session_token');
      
      await axios.post(`${API}/creator/subscription-settings`, {
        subscription_price: Number(settings.subscription_price),
        description: settings.description,
        benefits: settings.benefits
      }, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });

      toast.success('Subscription settings updated!');
      onClose();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const addBenefit = () => {
    if (newBenefit.trim() && !settings.benefits.includes(newBenefit.trim())) {
      setSettings(prev => ({
        ...prev,
        benefits: [...prev.benefits, newBenefit.trim()]
      }));
      setNewBenefit('');
    }
  };

  const removeBenefit = (index) => {
    setSettings(prev => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index)
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Crown className="h-5 w-5 text-purple-600" />
            <span>Subscription Settings</span>
          </DialogTitle>
          <DialogDescription>
            Set up your subscription offering for fans to follow your content
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Pricing */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Monthly Subscription Price</label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={settings.subscription_price}
                onChange={(e) => setSettings(prev => ({ ...prev, subscription_price: e.target.value }))}
                data-testid="creator-subscription-price-input"
                className="pl-10"
              />
            </div>
            <p className="text-xs text-gray-500">
              Set to $0.00 for free subscriptions. Paid subscriptions are processed through Stripe.
            </p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Subscription Description</label>
            <Textarea
              placeholder="Describe what subscribers will get..."
              value={settings.description}
              onChange={(e) => setSettings(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
            />
          </div>

          {/* Benefits */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Subscription Benefits</label>
            <div className="flex space-x-2">
              <Input
                placeholder="Add a benefit (e.g., Exclusive workouts)"
                value={newBenefit}
                onChange={(e) => setNewBenefit(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addBenefit()}
              />
              <Button onClick={addBenefit} disabled={!newBenefit.trim()}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            
            {(settings.benefits || []).length > 0 && (
              <div className="space-y-2 mt-4">
                {(settings.benefits || []).map((benefit, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm">{benefit}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeBenefit(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Preview */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <h4 className="font-medium mb-2">Preview</h4>
            <Card className="border-purple-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Monthly Subscription</CardTitle>
                  <Badge className={`${settings.subscription_price > 0 ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                    {Number(settings.subscription_price) > 0 ? `$${settings.subscription_price}/month` : 'Free'}
                  </Badge>
                </div>
                <CardDescription>{settings.description}</CardDescription>
              </CardHeader>
              {(settings.benefits || []).length > 0 && (
                <CardContent>
                  <div className="space-y-1">
                    {(settings.benefits || []).map((benefit, index) => (
                      <div key={index} className="flex items-center space-x-2 text-sm">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          </div>

          {/* Save Button */}
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                'Save Settings'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// User Subscriptions Management
export const UserSubscriptions = ({ open, onClose }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) {
      fetchSubscriptions();
    }
  }, [open]);

  const fetchSubscriptions = async () => {
    try {
      const sessionToken = Cookies.get('session_token');
      const response = await axios.get(`${API}/user/subscriptions`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setSubscriptions(Array.isArray(response.data) ? response.data : response.data?.subscriptions || []);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const cancelSubscription = async (subscriptionId, creatorName) => {
    try {
      const sessionToken = Cookies.get('session_token');
      const response = await axios.post(`${API}/subscriptions/${subscriptionId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      const msg = response.data?.message || `Unsubscribed from ${creatorName}`;
      toast.success(msg);
      fetchSubscriptions(); // Refresh list
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      const detail = error.response?.data?.detail || 'Failed to cancel subscription';
      toast.error(detail);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Users className="h-5 w-5 text-blue-600" />
            <span>My Subscriptions</span>
          </DialogTitle>
          <DialogDescription>
            Manage your creator subscriptions
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        ) : subscriptions.length === 0 ? (
          <div className="text-center py-8">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No subscriptions yet</p>
            <p className="text-sm text-gray-500">Start following your favorite creators!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {subscriptions.map((sub, index) => (
              <Card key={index} className="border-blue-100">
                <CardContent className="p-4">
                  <div className="flex justify-between items-center">
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2">
                        <h4 className="font-medium text-gray-900">{sub.creator_name || 'Creator'}</h4>
                        {sub.price > 0 && (
                          <Badge className="bg-purple-100 text-purple-700">
                            ${sub.price}/month
                          </Badge>
                        )}
                        <Badge variant="outline" className={`text-xs ${
                          sub.status === 'active' && sub.cancel_at_period_end ? 'text-orange-600 border-orange-200' :
                          sub.status === 'active' ? 'text-green-600 border-green-200' : 
                          sub.status === 'pending' ? 'text-yellow-600 border-yellow-200' : 
                          'text-gray-600 border-gray-200'
                        }`}>
                          {sub.status === 'active' && sub.cancel_at_period_end ? 'Cancelling' : (sub.status || 'active')}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600">
                        Subscribed: {sub.started_at ? new Date(sub.started_at).toLocaleDateString() : 'Date unavailable'}
                      </p>
                      {sub.current_period_end && (
                        <p className="text-xs text-gray-500">
                          {sub.cancel_at_period_end 
                            ? `Access until: ${new Date(sub.current_period_end).toLocaleDateString()}`
                            : `Next billing: ${new Date(sub.current_period_end).toLocaleDateString()}`
                          }
                        </p>
                      )}
                      {Array.isArray(sub.benefits) && sub.benefits.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <p className="text-xs font-medium text-gray-500 mb-1">Benefits:</p>
                          <ul className="text-xs text-gray-600 space-y-0.5">
                            {sub.benefits.map((b, bi) => (
                              <li key={bi} className="flex items-center gap-1">
                                <span className="text-green-500">&#10003;</span> {b}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      {sub.status === 'active' && !sub.cancel_at_period_end && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => cancelSubscription(sub.id, sub.creator_name)}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Unsubscribe
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Creator Subscribers Management
export const CreatorSubscribers = ({ open, onClose }) => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) {
      fetchSubscribers();
    }
  }, [open]);

  const fetchSubscribers = async () => {
    try {
      const sessionToken = Cookies.get('session_token');
      const response = await axios.get(`${API}/creator/subscribers`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setSubscribers(Array.isArray(response.data) ? response.data : response.data?.subscribers || []);
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      toast.error('Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Crown className="h-5 w-5 text-purple-600" />
            <span>Your Subscribers</span>
          </DialogTitle>
          <DialogDescription>
            View and manage your subscriber base
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        ) : subscribers.length === 0 ? (
          <div className="text-center py-8">
            <Crown className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No subscribers yet</p>
            <p className="text-sm text-gray-500">Share your content to attract subscribers!</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">Total Subscribers: {subscribers.length}</h3>
              <Badge className="bg-purple-100 text-purple-700">
                {subscribers.filter(s => s.price > 0).length} Paid
              </Badge>
            </div>
            
            {subscribers.map((sub, index) => (
              <Card key={index} className="border-purple-100">
                <CardContent className="p-4">
                  <div className="flex justify-between items-center">
                    <div className="space-y-1">
                      <h4 className="font-medium text-gray-900">{sub.user_name || 'Subscriber'}</h4>
                      <p className="text-xs text-gray-500">
                        Subscribed: {sub.started_at ? new Date(sub.started_at).toLocaleDateString() : 'Date unavailable'}
                      </p>
                    </div>
                    <div className="text-right">
                      {sub.price > 0 && (
                        <Badge className="bg-green-100 text-green-700">
                          ${sub.price}/month
                        </Badge>
                      )}
                      <div className="text-xs text-gray-500 mt-1">
                        Status: {sub.status || 'active'}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Subscription Success/Cancel Pages
export const SubscriptionSuccessPage = () => {
  const [status, setStatus] = useState('checking');
  const [details, setDetails] = useState(null);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get('session_id');
    if (!sessionId) {
      window.location.href = '/dashboard';
      return;
    }
    verifySession(sessionId, 0);
  }, []);

  useEffect(() => {
    if (status === 'success') {
      const timer = setTimeout(() => {
        window.location.href = '/dashboard';
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  const verifySession = async (sessionId, attempt) => {
    if (attempt >= 5) {
      setStatus('timeout');
      return;
    }
    try {
      const sessionToken = Cookies.get('session_token');
      const { data } = await axios.get(`${API}/payments/verify-session/${sessionId}`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setDetails(data);
      if (data.payment_status === 'paid') {
        setStatus('success');
      } else if (data.payment_status === 'expired') {
        setStatus('expired');
      } else {
        setTimeout(() => verifySession(sessionId, attempt + 1), 2500);
      }
    } catch (err) {
      console.error('Subscription verify error:', err);
      setStatus('error');
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full" data-testid="subscription-success-card">
          <CardContent className="text-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-purple-500 mx-auto mb-4" />
            <p className="text-gray-600" data-testid="subscription-status-message">Verifying your subscription...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full" data-testid="subscription-success-card">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle className="h-14 w-14 text-green-500" />
            </div>
            <CardTitle className="text-2xl text-green-600" data-testid="subscription-status-title">
              Subscription Activated!
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <div className="p-4 rounded-lg bg-green-50">
              <p className="text-gray-700" data-testid="subscription-status-message">
                {details?.creator_name
                  ? `You are now subscribed to ${details.creator_name}. Redirecting to your dashboard...`
                  : 'Subscription activated. Redirecting to your dashboard...'}
              </p>
            </div>
            {details && (
              <div className="text-sm text-gray-500">
                ${Number(details.amount).toFixed(2)}/month
              </div>
            )}
            <Button
              data-testid="go-to-dashboard-btn"
              onClick={() => window.location.href = '/dashboard'}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
            >
              <Crown className="h-4 w-4 mr-2" />
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error / timeout / expired states
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full" data-testid="subscription-success-card">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <XCircle className="h-14 w-14 text-red-500" />
          </div>
          <CardTitle className="text-xl text-red-600" data-testid="subscription-status-title">
            {status === 'expired' ? 'Session Expired' : 'Could Not Verify Subscription'}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <div className="p-4 rounded-lg bg-red-50">
            <p className="text-gray-700" data-testid="subscription-status-message">
              {status === 'expired'
                ? 'This checkout session has expired. Please try again.'
                : 'We could not verify your subscription. If you were charged, your access should appear shortly on your dashboard.'}
            </p>
          </div>
          <Button
            data-testid="go-to-dashboard-btn"
            onClick={() => window.location.href = '/dashboard'}
            variant="outline"
          >
            Go to Dashboard
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export const SubscriptionCancelledPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <XCircle className="h-16 w-16 text-red-500" />
          </div>
          <CardTitle className="text-2xl text-red-600">
            Subscription Cancelled
          </CardTitle>
        </CardHeader>
        
        <CardContent className="text-center space-y-4">
          <div className="p-4 rounded-lg bg-red-50">
            <p className="text-gray-700">
              Your subscription was cancelled. No charges were made to your account.
            </p>
          </div>

          <div className="flex gap-2 justify-center mt-6">
            <Button 
              onClick={() => window.location.href = '/dashboard'}
              variant="outline"
              className="flex items-center space-x-2"
            >
              Back to Dashboard
            </Button>
            
            <Button 
              onClick={() => window.history.back()}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
            >
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};